import { useState } from "react";
import {
    AddResourceCard,
    Button,
    Container,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Field,
    FieldGroup,
    FieldLabel,
    FieldSet,
    Grid,
    GridSkeleton,
    Input,
    ResourceCard,
    FilterBar,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Text,
    Textarea,
} from "@athena/ui";

/** Sentinel for "no filter" — Radix Select has no unset value, only a chosen one. */
const ALL = "__all__";

const RESOURCE_CATEGORIES = [
    "Mental Health",
    "Academics",
    "Student Life",
    "Counseling",
    "Health & Medical",
] as const;
type ResourceCategory = (typeof RESOURCE_CATEGORIES)[number];
import { SquareMenu } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@apollo/client/react";
import {
    OrganizationResourcesDocument,
    CreateOrganizationResourceDocument,
    UpdateOrganizationResourceDocument,
    DeleteOrganizationResourceDocument,
} from "@/graphql/resources.generated";
import type { OrganizationResourcesQuery } from "@/graphql/resources.generated";
import { DeleteDialog } from "@/components/DeleteDialog";

type OrgResource = OrganizationResourcesQuery["organizationResources"][number];

interface OrganizationResourceProps {
    orgId: string;
    /** Controls the create-resource dialog externally (e.g. from a page-header button). Uncontrolled if omitted. */
    createOpen?: boolean;
    onCreateOpenChange?: (open: boolean) => void;
}

export function OrganizationResource({
    orgId,
    createOpen: createOpenProp,
    onCreateOpenChange,
}: OrganizationResourceProps) {
    const { t } = useTranslation();
    const { data, loading } = useQuery(OrganizationResourcesDocument, {
        variables: { organizationId: orgId },
    });

    // Create dialog
    const [
        createOpenState,
        setCreateOpenState,
    ] = useState<boolean>(false);
    const createOpen = createOpenProp ?? createOpenState;
    const setCreateOpen = onCreateOpenChange ?? setCreateOpenState;
    const [
        title,
        setTitle,
    ] = useState<string>("");
    const [
        description,
        setDescription,
    ] = useState<string>("");
    const [
        url,
        setUrl,
    ] = useState<string>("");
    const [
        phoneNumber,
        setPhoneNumber,
    ] = useState<string>("");
    const [
        emailAddress,
        setEmailAddress,
    ] = useState<string>("");
    const [
        physicalAddress,
        setPhysicalAddress,
    ] = useState<string>("");
    const [
        category,
        setCategory,
    ] = useState<ResourceCategory | "">("");

    // Edit dialog
    const [
        editTarget,
        setEditTarget,
    ] = useState<OrgResource | null>(null);
    const [
        editTitle,
        setEditTitle,
    ] = useState<string>("");
    const [
        editDescription,
        setEditDescription,
    ] = useState<string>("");
    const [
        editUrl,
        setEditUrl,
    ] = useState<string>("");
    const [
        editPhoneNumber,
        setEditPhoneNumber,
    ] = useState<string>("");
    const [
        editEmailAddress,
        setEditEmailAddress,
    ] = useState<string>("");
    const [
        editPhysicalAddress,
        setEditPhysicalAddress,
    ] = useState<string>("");
    const [
        editCategory,
        setEditCategory,
    ] = useState<ResourceCategory | "">("");

    // Delete dialog
    const [
        deleteTarget,
        setDeleteTarget,
    ] = useState<OrgResource | null>(null);

    const [
        createResource,
        { loading: creating },
    ] = useMutation(CreateOrganizationResourceDocument, {
        refetchQueries: [{ query: OrganizationResourcesDocument, variables: { organizationId: orgId } }],
    });

    const [
        updateResource,
        { loading: updating },
    ] = useMutation(UpdateOrganizationResourceDocument);

    const [
        deleteResource,
        { loading: deleting },
    ] = useMutation(DeleteOrganizationResourceDocument);

    const resetCreateForm = () => {
        setTitle("");
        setDescription("");
        setUrl("");
        setPhoneNumber("");
        setEmailAddress("");
        setPhysicalAddress("");
        setCategory("");
    };

    const openEdit = (resource: OrgResource) => {
        setEditTarget(resource);
        setEditTitle(resource.title);
        setEditDescription(resource.description ?? "");
        setEditUrl(resource.url ?? "");
        setEditPhoneNumber(resource.phoneNumber ?? "");
        setEditEmailAddress(resource.emailAddress ?? "");
        setEditPhysicalAddress(resource.physicalAddress ?? "");
        setEditCategory((resource.category as ResourceCategory) ?? "");
    };

    const handleCreate = async () => {
        if (!title.trim()) return;
        await createResource({
            variables: {
                input: {
                    organizationId: orgId,
                    title: title.trim(),
                    description: description.trim() || undefined,
                    url: url.trim() || undefined,
                    phoneNumber: phoneNumber.trim() || undefined,
                    emailAddress: emailAddress.trim() || undefined,
                    physicalAddress: physicalAddress.trim() || undefined,
                    category: category.trim() || undefined,
                },
            },
        });
        setCreateOpen(false);
        resetCreateForm();
    };

    const handleUpdate = async () => {
        if (!editTarget || !editTitle.trim()) return;
        await updateResource({
            variables: {
                id: editTarget.id,
                input: {
                    title: editTitle.trim(),
                    description: editDescription.trim() || undefined,
                    url: editUrl.trim() || undefined,
                    phoneNumber: editPhoneNumber.trim() || undefined,
                    emailAddress: editEmailAddress.trim() || undefined,
                    physicalAddress: editPhysicalAddress.trim() || undefined,
                    category: editCategory || undefined,
                },
            },
            optimisticResponse: {
                updateOrganizationResource: {
                    __typename: "OrganizationResource",
                    id: editTarget.id,
                    title: editTitle.trim(),
                    description: editDescription.trim() || null,
                    url: editUrl.trim() || null,
                    phoneNumber: editPhoneNumber.trim() || null,
                    emailAddress: editEmailAddress.trim() || null,
                    physicalAddress: editPhysicalAddress.trim() || null,
                    category: editCategory || null,
                    createdAt: editTarget.createdAt,
                },
            },
        });
        setEditTarget(null);
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        await deleteResource({
            variables: { id: deleteTarget.id },
            update(cache) {
                cache.evict({ id: cache.identify({ __typename: "OrganizationResource", id: deleteTarget.id }) });
                cache.gc();
            },
        });
        setDeleteTarget(null);
    };

    const resources = data?.organizationResources ?? [];

    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        categoryFilter,
        setCategoryFilter,
    ] = useState<string>(ALL);

    /**
     * Filtered in the browser: an organization's resources arrive whole from one query, so there is
     * no page boundary for a search to fall off. Description as well as title, because a resource is
     * often remembered by what it offers rather than by what it is called.
     */
    const term = search.trim().toLowerCase();
    const visibleResources = resources.filter((resource) => {
        if (categoryFilter !== ALL && resource.category !== categoryFilter) return false;
        return !term || `${resource.title} ${resource.description ?? ""}`.toLowerCase().includes(term);
    });
    const filtersActive = term !== "" || categoryFilter !== ALL;

    if (loading) {
        return <GridSkeleton />;
    }

    const resourceFormFields = (fields: {
        title: string;
        setTitle: (v: string) => void;
        description: string;
        setDescription: (v: string) => void;
        url: string;
        setUrl: (v: string) => void;
        phoneNumber: string;
        setPhoneNumber: (v: string) => void;
        emailAddress: string;
        setEmailAddress: (v: string) => void;
        physicalAddress: string;
        setPhysicalAddress: (v: string) => void;
        category: ResourceCategory | "";
        setCategory: (v: ResourceCategory | "") => void;
        idPrefix: string;
    }) => (
        <FieldSet>
            <FieldGroup>
                <Field>
                    <FieldLabel htmlFor={`${fields.idPrefix}-title`} required>
                        Title
                    </FieldLabel>
                    <Input
                        id={`${fields.idPrefix}-title`}
                        placeholder="e.g. Employee Assistance Program"
                        value={fields.title}
                        onChange={(e) => fields.setTitle(e.target.value)}
                        autoFocus
                        type="text"
                    />
                </Field>
                <Field>
                    <FieldLabel htmlFor={`${fields.idPrefix}-description`}>Description</FieldLabel>
                    <Textarea
                        id={`${fields.idPrefix}-description`}
                        placeholder="Brief description of this resource..."
                        value={fields.description}
                        onChange={(e) => fields.setDescription(e.target.value)}
                        rows={3}
                    />
                </Field>
                <Field>
                    <FieldLabel htmlFor={`${fields.idPrefix}-url`}>URL</FieldLabel>
                    <Input
                        id={`${fields.idPrefix}-url`}
                        placeholder="https://..."
                        value={fields.url}
                        onChange={(e) => fields.setUrl(e.target.value)}
                        type="text"
                    />
                </Field>
                <Field>
                    <FieldLabel htmlFor={`${fields.idPrefix}-phone`}>Phone number</FieldLabel>
                    <Input
                        id={`${fields.idPrefix}-phone`}
                        placeholder="e.g. 1-800-555-0100"
                        value={fields.phoneNumber}
                        onChange={(e) => fields.setPhoneNumber(e.target.value)}
                    />
                </Field>
                <Field>
                    <FieldLabel htmlFor={`${fields.idPrefix}-email`}>Email address</FieldLabel>
                    <Input
                        id={`${fields.idPrefix}-email`}
                        type="email"
                        placeholder="contact@example.com"
                        value={fields.emailAddress}
                        onChange={(e) => fields.setEmailAddress(e.target.value)}
                    />
                </Field>
                <Field>
                    <FieldLabel htmlFor={`${fields.idPrefix}-address`}>Physical address</FieldLabel>
                    <Input
                        id={`${fields.idPrefix}-address`}
                        placeholder="123 Main St, City, State"
                        value={fields.physicalAddress}
                        onChange={(e) => fields.setPhysicalAddress(e.target.value)}
                        type="text"
                    />
                </Field>
                <Field>
                    <FieldLabel htmlFor={`${fields.idPrefix}-category`}>Category</FieldLabel>
                    <Select value={fields.category} onValueChange={(v) => fields.setCategory(v as ResourceCategory)}>
                        <SelectTrigger id={`${fields.idPrefix}-category`} className="w-full">
                            <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                            {RESOURCE_CATEGORIES.map((c) => (
                                <SelectItem key={c} value={c}>
                                    {c}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
            </FieldGroup>
        </FieldSet>
    );

    return (
        <Container>
            {resources.length > 0 && (
                <FilterBar
                    search={search}
                    onSearchChange={setSearch}
                    searchLabel="Search resources"
                    searchPlaceholder="Search by title or description"
                    searchClassName="max-w-xs"
                    active={filtersActive}
                    onClear={() => {
                        setSearch("");
                        setCategoryFilter(ALL);
                    }}
                    count={{ shown: visibleResources.length, total: resources.length }}
                >
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger className="w-48" aria-label="Filter by category">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL}>All categories</SelectItem>
                            {RESOURCE_CATEGORIES.map((category) => (
                                <SelectItem key={category} value={category}>
                                    {category}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </FilterBar>
            )}
            {resources.length > 0 ? (
                <Grid cols={3} gap="md" className="w-full">
                    {visibleResources.map((resource) => (
                        <ResourceCard
                            key={resource.id}
                            resource={{
                                title: resource.title,
                                description: resource.description,
                                url: resource.url,
                                category: resource.category,
                                addedAt: new Date(resource.createdAt).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                }),
                            }}
                            onEdit={() => openEdit(resource)}
                            onDelete={() => setDeleteTarget(resource)}
                        />
                    ))}
                    <AddResourceCard
                        label={t("admin.resources.addResource")}
                        subLabel={t("admin.resources.addResourceSubtitle")}
                        onClick={() => setCreateOpen(true)}
                    />
                </Grid>
            ) : (
                <div className="flex flex-col items-center justify-center gap-3 py-32">
                    <div className="bg-muted flex h-12 w-12 items-center justify-center rounded-xl">
                        <SquareMenu className="text-muted-foreground h-6 w-6" />
                    </div>
                    <Text weight="medium">No resources yet</Text>
                    <Text muted className="text-sm">
                        Add your first resource to share with members of this organization.
                    </Text>
                </div>
            )}

            {/* Create dialog */}
            <Dialog
                open={createOpen}
                onOpenChange={(o) => {
                    if (!o) {
                        setCreateOpen(false);
                        resetCreateForm();
                    }
                }}
            >
                <DialogContent className="max-h-[70vh] max-w-[70vw] overflow-scroll">
                    <DialogHeader>
                        <DialogTitle>New resource</DialogTitle>
                        <DialogDescription>
                            Add a resource that will be shared with members of this organization.
                        </DialogDescription>
                    </DialogHeader>
                    {resourceFormFields({
                        title,
                        setTitle,
                        description,
                        setDescription,
                        url,
                        setUrl,
                        phoneNumber,
                        setPhoneNumber,
                        emailAddress,
                        setEmailAddress,
                        physicalAddress,
                        setPhysicalAddress,
                        category,
                        setCategory,
                        idPrefix: "res",
                    })}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setCreateOpen(false);
                                resetCreateForm();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button disabled={!title.trim() || creating} onClick={handleCreate}>
                            {creating ? "Saving…" : "Add resource"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit dialog */}
            <Dialog
                open={!!editTarget}
                onOpenChange={(o) => {
                    if (!o) setEditTarget(null);
                }}
            >
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Edit resource</DialogTitle>
                        <DialogDescription>Update the details for this resource.</DialogDescription>
                    </DialogHeader>
                    {resourceFormFields({
                        title: editTitle,
                        setTitle: setEditTitle,
                        description: editDescription,
                        setDescription: setEditDescription,
                        url: editUrl,
                        setUrl: setEditUrl,
                        phoneNumber: editPhoneNumber,
                        setPhoneNumber: setEditPhoneNumber,
                        emailAddress: editEmailAddress,
                        setEmailAddress: setEditEmailAddress,
                        physicalAddress: editPhysicalAddress,
                        setPhysicalAddress: setEditPhysicalAddress,
                        category: editCategory,
                        setCategory: setEditCategory,
                        idPrefix: "edit-res",
                    })}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditTarget(null)}>
                            Cancel
                        </Button>
                        <Button disabled={!editTitle.trim() || updating} onClick={handleUpdate}>
                            {updating ? "Saving…" : "Save changes"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <DeleteDialog
                open={!!deleteTarget}
                onOpenChange={(o) => {
                    if (!o) setDeleteTarget(null);
                }}
                title="Delete resource"
                deleteTarget={deleteTarget?.title}
                onConfirm={handleDelete}
                loading={deleting}
            />
        </Container>
    );
}
